import 'whatwg-fetch';
import 'classlist-polyfill';
import qs from 'qs';
import {site_url} from './utils/urls';

/*************************************************************************
    Counter
**************************************************************************/

export class Counter {
    
    constructor(params) {
        this.target = params.target;
        if (this.target.firstChild === null) {
            this.target.appendChild(document.createTextNode('0'))
        }
        this.dispatcher = params.dispatcher || new Dispatcher(this);
    }

    get_value() {
        return parseInt(this.target.firstChild.data);
    }

    set_value(value) {
        const css_cls = value > 0 ? 'positive' : 'zero_or_neg';

        this.target.firstChild.data = value;
        this.target.classList.add('counter_' + css_cls);

        this.dispatcher.fire('after_counter_value_change', {
            value : value
        });
    }

    inc(value) {
        this.set_value(this.get_value() + (value ? value : 1));
    }

    dec(value) {
        this.set_value(this.get_value() - (value ? value : 1));
    }

    reset() {
        this.set_value(0);
    }

    checked(src) {
        src.checked ? this.inc() : this.dec();
    }
}


/*************************************************************************
    Clipboard
**************************************************************************/

export class Clipboard {
    
    constructor(params) {
        this.container = params.container;
        this.cpt = new Counter({target: document.getElementById('clipboard-counter')})
        this.dispatcher = params.dispatcher || new Dispatcher(this);
        this.toggle_button = this.container.querySelector('a[data-toggle="dropdown"]');

        document.getElementById('clipboard-clear').addEventListener('click', (e) => {
            e.preventDefault();
            this.clear();
        });

        document.getElementById('clipboard-paste').addEventListener('click', (e) => {
            e.preventDefault();
            this.dispatcher.fire('clipboard_paste', { evt: e } );
        });

        if (this.ids) {
            this.cpt.set_value(this.ids.length);
        } else {
            this.cpt.reset();
            this.container.style.display = 'none';
        }
    }

    clear() {
        sessionStorage.removeItem('ids');
        this.cpt.reset();
        this.container.style.display = 'none';
    }

    set ids (value) {
        try {
            sessionStorage.setItem('ids', JSON.stringify(value));
            this.cpt.set_value(value.length);
            this.container.style.display = '';
        } catch (e) {
            console.log(e);
        }
    }

    get ids() {
        return JSON.parse(sessionStorage.getItem('ids'));
    }
}

/*************************************************************************
    Dispatcher
**************************************************************************/

export class Dispatcher {
    constructor(scope) {
        this.default_scope = scope || this;
        this.callbacks = {};
    }


    add(evt, callback, scope) {
        if (!this.callbacks[evt]) {
            this.callbacks[evt] = [];
        }

        this.callbacks[evt].push({
            'callback' : callback,
            'scope' : scope || this.default_scope
        });
    }

    remove(evt) {
        if (this.callbacks[evt]) {
            delete this.callbacks[evt];
        }
    }

    fire(evt, params) {
        const cbs = this.callbacks[evt];

        if (!cbs) {
            return false;
        }

        for (let i=0, _len=cbs.length; i<_len; i++) {
            cbs[i].callback.call(cbs[i].scope, params, arguments);
        }
    }

}

/*************************************************************************
    Pagination
**************************************************************************/

export class Pagination {

    constructor(params) {
        this.dispatcher = params.dispatcher || new Dispatcher(this);
        this.set('limit', params.limit || 10);
        this.set('offset', 0);
    }

    set(key, value) {
        if (this._values === undefined) {
            this._values = {};
        }

        switch(key) {
            case 'offset':
            case 'limit':
            case 'count':
                value = parseInt(value);

                if (value >= 0 && this.get(key) != value) {
                    this._values[key] = value;
                    this._values.page_current = this._values.offset / this._values.limit + 1;
                    this._values.page_total = Math.floor(this._values.count / this._values.limit);

                    if (this._values.count % this._values.limit != 0) {
                        this._values.page_total++;
                    }

                    this.dispatcher.fire(key + '_change', {
                        value: value
                    });
                }

                break;
        }
    }

    get(key) {
        return this._values[key];
    }

    first() {
        this.page(1);
    }

    previous() {
        this.page(this.get('page_current') - 1);
    }

    page(idx) {
        idx = parseInt(idx);

        if (idx > this.get('page_total')) {
            idx = this.get('page_total');
        } else if (idx <= 0 || isNaN(idx)) {
            idx = 1;
        }

        this.set('offset', (idx - 1) * this.get('limit'));

        this.dispatcher.fire('pagination_change', {
            values: this._values
        });
    }

    next() {
        this.page(this.get('page_current') + 1);
    }

    last() {
        this.page(this.get('page_total'));
    }
}

/*************************************************************************
   ResponseParserComponent
**************************************************************************/

class ResponseParserComponent {

    constructor(params) {
        this.dispatcher = params.dispatcher || new Dispatcher(this);
        this.components = params.components;
    }

    parse_components(node) {
        /* node is the <components> */
        if (node.hasChildNodes()) {
            for (const elem of node.childNodes) {
                /* check if elem is an Element */
                if (elem.nodeType == 1) {
                    const component = this.components[elem.nodeName];
                    if (component) {
                        component.update_from_xml(elem);
                    }
                }
            }
        }
    }

    parse(xml_doc, level=0) {
        /* Start at the root (<response>) */

        for (const elem of xml_doc.childNodes) {
            if (elem.nodeName == 'response' && level == 0) {
                /* Parse <response> */
                this.parse(elem, level+1);
            } else if (elem.nodeName == 'meta' && level == 1) {
                // TODO
            } else if (elem.nodeName == 'errors' && level == 2) {
                // TODO
            } else if (elem.nodeName == 'components' && level == 1) {
                /* Parse <response> -> <components> */
                this.parse_components(elem);
            } else {
                // nothing
            }
        }
    }
}


/*************************************************************************
   Component
**************************************************************************/

export class Component {

    constructor(params) {
        if (params) {
            this.container = params.container;
            this.dispatcher = params.dispatcher || new Dispatcher(this);
        }
    }

    update_from_xml(node) {
        if (node.hasChildNodes()) {
            while (this.container.firstChild) {
                this.container.removeChild(this.container.firstChild);
            }

            for (const elem of node.childNodes) {
                this.container.appendChild(elem.cloneNode(true));
            }
        }

        this.dispatcher.fire('after_update', {
            node: node
        });
    }

}

/*************************************************************************
    NavigationComponent
**************************************************************************/

export class NavigationComponent extends Component {
    constructor(params) {
        super(params);
        this.dispatcher.add('after_update', function() {
            this.add_handlers();
        });
    }

    add_handlers() {
    }
}

/*************************************************************************
    PaginationComponent
**************************************************************************/


export class PaginationComponent extends Component {

    constructor(params) {
        super(params);
        this.pagination = params.pagination || new Pagination({});
        this.dispatcher.add('after_update', function() {
            this.add_handlers();
        });
    }

    update_from_xml(node) {
        this.pagination.set('count', node.getAttribute('count'));
        this.pagination.set('offset', node.getAttribute('offset'));
        this.pagination.set('limit', node.getAttribute('limit'));
        super.update_from_xml(node);
    }

    add_handlers() {
        const links = this.container.getElementsByTagName('a');

        for (const elem of links) {
            const direction = elem.getAttribute('data-direction');
            if (direction) {
                elem.addEventListener('click', e => {
                    e.preventDefault();

                    if (direction == 'goto') {
                        const page = elem.getAttribute('data-page');
                        this.pagination.page(page);
                    } else {
                        this.pagination[direction]();
                    }
                });
            }
        }
    }
}


/*************************************************************************
    FolderAdminComponent
**************************************************************************/

export class FolderAdminComponent extends Component {
    constructor(params) {
        super(params);
        this.dispatcher.add('after_update', function() {
            this.add_action_handlers();
            this.add_action_handlers({
                selection: this.container.getElementsByTagName('input')
            });
        });
    }

    _get_data(src) {
        /* This function returns the row container (tr) and the object data */
        let data = {};

        do {
            if (src.nodeType !== 1) {
                return data;
            }

            if (src.hasAttribute('data-obj')) {
                data.obj = JSON.parse(src.getAttribute('data-obj'));
                data.container = src;
                return data;
            }

            src = src.parentNode;

        } while (1)
    }

    toggle_select_objs(selection) {
        /* With a given selection of id (ex: [1,5,56,32,1541]) it selects or
         * deselects the corresponding object in the view */

        if (selection && selection.length > 0) {
            const inputs = this.container.getElementsByTagName('input');
            let cpt = 0;

            for (let i=0, _ilen = inputs.length, _slen = selection.length; i<_ilen; i++) {
                /* For each <input> tag check that it has a
                 * data-purpose="select_object" attribute */
                const input = inputs[i];
                if (input.getAttribute('data-purpose') == 'select_object') {
                    const data = this._get_data(input);
                    if (data.obj && data.obj.id) {
                        for (let j=0; j<_slen; j++) {
                            if (selection[j] == data.obj.id) {
                                input.checked = !input.checked;
                                cpt ++;
                                this.dispatcher.fire('select_object', {
                                    obj: data.obj,
                                    container : data.container,
                                    src: input
                                });
                                break;
                            }
                        }
                    }
                }

                // small optimization
                if (cpt === _slen) {
                    break;
                }
            }
        }
    }

    // Handlers

    add_action_handlers(p) {
        /* This function add handlers on all p.selection tags which have a data-purpose
         * attribute. When clicked, the corresponding event is fired */

        p = p || {};

        const links = p.selection || this.container.getElementsByTagName('a');

        for (const el of links) {
            if (el.hasAttribute('data-purpose')) {
                el.addEventListener(p.evt || 'click', (e) => {
                    if (el.tagName == 'A') {
                        e.preventDefault();
                    }

                    const data = this._get_data(el);
                    if (data) {
                        this.dispatcher.fire(el.getAttribute('data-purpose'), {
                            obj: data.obj,
                            container: data.container,
                            src: e.target
                        });
                    }
                });
            }
        }
    }
}


/*************************************************************************
    FolderController
**************************************************************************/

export class FolderController {

    constructor(params) {
        if (params) {
            this.url = params.url;
            this.folder = params.folder;
            this.dispatcher = params.dispatcher || new Dispatcher(this);
            this.parameters = params.parameters || {};
            this.components = params.components;
            this.selection = [];

            if (!this.parameters.filters) {
                this.parameters.filters = [];
            }

            this.response_parser = new ResponseParserComponent({
                components: params.components
            });

            this.dispatcher.add('change_weight_success', () => {
                delete(this.selection_move);
                this.refresh({});
            });

            this.dispatcher.add('after_delete_success', () => {
                this.refresh({});
                this.selection = [];
            });
        }
    }


    add_selection(obj_id) {
        if (this.selection.indexOf(obj_id) === -1) {
            this.selection.push(obj_id);
        }
    }

    remove_selection(obj_id) {
        const idx = this.selection.indexOf(obj_id);

        if (idx !== -1) {
            this.selection.splice(idx, 1);
        }
    }

    clear_selection() {
        for (var i=0, _len = this.selection.length; i<_len; i++) {
            this.selection.pop();
        }
    }

    add_sort(p) {
        if (!this.sort) {
            this.sort = [];
        }

        this.sort.push(p);
        this.refresh();
    }

    add_filter(filter) {
        if (!this.has_filter(filter)) {
            this.parameters.filters.push(filter);
        }
    }

    has_filter(filter) {
        return this.parameters.filters.indexOf(filter) !== -1;
    }

    remove_filter(filter) {
        var idx = this.parameters.filters.indexOf(filter);

        if (idx !== -1) {
            this.parameters.filters.splice(idx, 1);
        }
    }

    is_filtered() {
        return this.parameters.filters.length > 0;
    }

    load() {
        var _self = this;

        this.dispatcher.fire('before_load');

        this.parameters.components = [];
        for (var i in this.components) {
            this.parameters.components.push(i);
        }

        if (this.sort) { 
            this.parameters.sorts = [];
            this.sort.forEach(function(sort, cpt) {
                let s = '__s' + cpt;
                _self.parameters[s] = sort.key;
                _self.parameters[s + 'direction'] = sort.direction
                _self.parameters[s + 'nulls'] = sort.nulls
            });
        }

        const url = this.url + qs.stringify(this.parameters, {
                addQueryPrefix: true,
                arrayFormat: 'repeat'
            }),
            options = {
                method: 'GET',
                credentials: 'same-origin',
                headers: {
                    'Accept': 'application/xml'
                }
            };

        fetch(url, options)
            .then(response => response.ok ? response.text() : Promise.reject({
				status: response.status,
				statusText: response.statusText
			}))
            .then(str => (new window.DOMParser()).parseFromString(str, "application/xml"))
            .then(data => {
                this.response_parser.parse(data);
                this.dispatcher.fire('after_load_success');
            }).catch(error => console.log(error));
    }

    refresh(params) {
        for (var i in params) {
            this.parameters[i] = params[i];
        }

        this.load();
    }

    move(params) {
        if (!this.selection_move) {
            this.selection_move = params;
            params.container.classList.add('info');
        } else if (this.selection_move == params.container) {
            delete(this.selection_move);
            params.container.classList.remove('info');
        } else {
            params.container.classList.add('success');
            Content.change_weight({
                from: this.selection_move,
                to: params,
                dispatcher: this.dispatcher
            });
        }
    }

    add_delete_selection_handler(src) {
        src.addEventListener('click', e => {
            e.preventDefault();
            this.folder.bulk_delete(this.selection, this.dispatcher)
        });
    }

    add_sort_handler(src) {
        const links = src.getElementsByTagName('a');

        for (const el of links) {
             const sort = el.getAttribute('data-sort');
             if (sort) {
                 el.addEventListener('click', e => {
                    e.preventDefault();

                    if (this.parameters.__s0 == sort) {
                        if (this.parameters.__s0direction == 'asc') {
                            this.parameters.__s0direction = 'desc';
                        } else {
                            this.parameters.__s0direction = 'asc';
                        }
                    } else {
                        this.parameters.__s0direction = 'asc';
                    }

                     this.refresh({
                        __s0 : sort
                     });

                     this.dispatcher.fire('after_sort_clicked', {
                         src : el
                     });
                 });
             }
        }
    }
    
}

/*************************************************************************
    AccountBrowserController
**************************************************************************/

export class BrowserController {

    constructor(params) {
        if (params) {
            this.url = params.url;
            this.dispatcher = params.dispatcher || new Dispatcher(this);
            this.parameters = params.parameters || {};
            this.components = params.components;
            this.selection = [];

            if (!this.parameters.filters) {
                this.parameters.filters = [];
            }

            this.response_parser = new ResponseParserComponent({
                components: params.components
            });

            this.dispatcher.add('after_delete_success', () => {
                this.refresh({});
                this.selection = [];
            });
        }
    }

    add_selection(obj_id) {
        if (this.selection.indexOf(obj_id) === -1) {
            this.selection.push(obj_id);
        }
    }

    remove_selection(obj_id) {
        const idx = this.selection.indexOf(obj_id);

        if (idx !== -1) {
            this.selection.splice(idx, 1);
        }
    }

    clear_selection() {
        for (var i=0, _len = this.selection.length; i<_len; i++) {
            this.selection.pop();
        }
    }

    add_filter(filter) {
        if (!this.has_filter(filter)) {
            this.parameters.filters.push(filter);
        }
    }

    has_filter(filter) {
        return this.parameters.filters.indexOf(filter) !== -1;
    }

    remove_filter(filter) {
        const idx = this.parameters.filters.indexOf(filter);

        if (idx !== -1) {
            this.parameters.filters.splice(idx, 1);
        }
    }

    is_filtered() {
        return this.parameters.filters.length > 0;
    }

    load() {
        this.dispatcher.fire('before_load');

        this.parameters.components = [];
        for (const i in this.components) {
            this.parameters.components.push(i);
        }

        const url = this.url + qs.stringify(this.parameters, {
                addQueryPrefix: true,
                arrayFormat: 'repeat'
            }),
            options = {
                method: 'GET',
                credentials: 'same-origin',
                headers: {
                    'Accept': 'application/xml'
                }
            };

        return new Promise((resolve, reject) => {
            fetch(url, options).then(
                response => response.ok ? response.text() : Promise.reject(response)
            ).then(
                str => (new window.DOMParser()).parseFromString(str, "application/xml")
            ).then(
                data => {
                    this.response_parser.parse(data);
                    this.dispatcher.fire('after_load_success');
                }
            ).then(
                () => resolve() 
            ).catch(
                error => console.log(error)
            );
        })
    }

    refresh(params) {
        for (var i in params) {
            this.parameters[i] = params[i];
        }

        this.load();
    }

    add_delete_selection_handler(src) {
        src.addEventListener('click', e => {
            e.preventDefault();
            console.log('TODO');
        });
    }

}

/*************************************************************************
    Content
**************************************************************************/

class Content {

    constructor(params) {
        this.load(params);
        this.dispatcher = params.dispatcher || new Dispatcher(this);
    }

    /* Initialize an object */
    load(params) {
        for (const key in params) {
            this[key] = params[key];
        }
    }

    static change_weight(p) {
        const url = site_url(p.from.obj.id, 'weight'),
            csrf = document.body.getAttribute('data-csrf'),
            body = new FormData(),
            headers = { 
                "Accept": "application/json", 
                "X-CSRF-Token": csrf   
            };

        body.set('weight', p.to.obj.weight);

        fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: headers,
            body: body
        }).then(
            response => response.ok ? response.json() : Promise.reject(response)
        ).then(
            json => p.dispatcher.fire('change_weight_success')
        ).catch(response => {
            p.dispatcher.fire('change_weight_error', {
                from: p.from,
                to: p.to
            });
            console.log('change_weight() error');
        });
    }
}

/*************************************************************************
    Folder
**************************************************************************/

export class Folder extends Content {

    bulk_delete(selection, dispatcher) {
        if (selection.length > 0 && confirm('Are you sure?')) {
            const url = site_url(this.id, 'bulk_delete');
            const csrf = document.body.getAttribute('data-csrf');
            const headers = {
                "Accept": "application/json", 
                "X-CSRF-Token": csrf   
            };
            const body = new FormData();

            for (const id of selection) {
                body.append('ids', id);
            }

            fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: headers,
                body: body
            }).then(
                response => response.ok ? response.json() : Promise.reject(response)
            ).then(
                json => dispatcher.fire('after_delete_success', {
                    data: json
                })
            ).catch(response => {
                dispatcher.fire('after_delete_error');
            })
        }
    }
 
    paste(ids) {
        if (confirm('Are you sure that you want to move those items ? They will me moved from their original location !')) {

            const url = site_url(this.id, 'paste'),
                csrf = document.body.getAttribute('data-csrf'),
                headers = {
                    "Accept": "application/json", 
                    "Content-Type": "application/x-www-form-urlencoded",
                    "X-CSRF-Token": csrf   
                },
                body = qs.stringify({ids: ids}, {arrayFormat: 'repeat'});

            fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: headers,
                body: body
            }).then(
                response => response.ok ? response.json() : Promise.reject(response)
            ).then(
                json => this.dispatcher.fire('after_paste_success', {
                    data: json
                })
            ).catch(response => {
                this.dispatcher.fire('after_paste_error');
                console.log(response)
            })
        }
    }
}

export class Event extends Content {
    
    constructor(params) {
        super(params);
    }

    is_georeferenced() {
        return this.address_latitude && this.address_longitude;
    }
}

/*************************************************************************
    TagList
**************************************************************************/

export class TagList {
    
    constructor(params) {
        this.container = params.container;
    }

    load() {
        const url = this.url + qs.stringify(this.parameters, {
                addQueryPrefix: true,
                arrayFormat: 'repeat'
            }),
            options = {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            };

        fetch(this.url, options)
            .then(response => response.ok ? response.json() : Promise.reject({
				status: response.status,
				statusText: response.statusText
			}))
            .then(json => {
                while (this.container.firstChild) {
                    this.container.removeChild(this.container.firstChild);
                }

                this.build_select(data);
                this.dispatcher.fire('after_load_success');
            }).catch(error => console.log(error));
    }

    build_select(data) {
        for (var i=0, _len = data.length; i<_len; i++) {
            const result = data[i];
            this.add_tag(result[0], result[1]);
        }
    }

    add_tag(tag, selected) {
        const opt = document.createElement('option');

        opt.setAttribute('value', tag.id);
        
        if (selected) {
            opt.setAttribute('selected', 'selected');
        }

        opt.appendChild(document.createTextNode(tag.name));

        this.container.appendChild(opt);

        return opt;
    }
}


/*************************************************************************
    Tag
**************************************************************************/

export class Tag {
    
    constructor(params) {
        if (params) {
            for (const key in params) {
                this[key] = params[key];
            }
        }

        if (!this.dispatcher) {
            this.dispatcher = new Dispatcher(this);
        }
    }

    save(params) {
        const csrf = document.body.getAttribute('data-csrf'),
            data = new FormData();

        data.set('name', this.data.name);

        fetch(site_url('tag'), {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-cache',
            headers: {
                'Accept': 'application/json',
                'X-CSRF-Token': csrf
            },
            body: data
        }).then(
            response => response.ok ? response.json() : Promise.reject({
                status: response.status,
                statusText: response.statusText
            })
        ).then(json => {
            this.dispatcher.fire('after_save_success', {
                tag: json
            });
        });
    }
}

/*************************************************************************
    Permission
**************************************************************************/


export class ACLBrowser extends BrowserController {

    constructor(params) {
        super(params);
        this.manage_url = params.manage_url;
        this.csrf = params.csrf;
        
        if (!params.manage_selectors) {
            this.manage_selectors = [
                "a[data-control='move']",
                "a[data-control='delete']"
            ]
        } else {
            this.manage_selectors = params.manage_selectors;
        }
    }

    _get_data(src) {
        /* This function returns the row container (tr) and the object data */
        let data = {};

        do {
            if (src.nodeType !== 1) {
                return data;
            }

            if (src.hasAttribute('data-obj')) {
                data.obj = JSON.parse(src.getAttribute('data-obj'));
                data.container = src;
                return data;
            }

            src = src.parentNode;

        } while (1)
    }

    order(params) {
        if (!this.selection_move) {
            this.selection_move = params;
            params.container.classList.add('info');
        } else if (this.selection_move == params.container) {
            delete(this.selection_move);
            params.container.classList.remove('info');
        } else {
            params.container.classList.add('success');
            this.change_weight(
                this.selection_move.obj.role_id,
                this.selection_move.obj.resource,
                this.selection_move.obj.permission_id,
                params.obj.weight
            ).then(
                this.selection_move = undefined
            ).then(
                this.load()
            )
        }
    }

    add_handlers() {
        const selectors = this.manage_selectors;

        for (const a of this.components.main.container.querySelectorAll(selectors)) {
            a.addEventListener('click', (e) => {
                e.preventDefault();

                const target = e.currentTarget;
                const data = this._get_data(target);

                switch (target.getAttribute('data-control')) {
                    case 'move':
                        this.order(data);
                        break;
                    case 'delete':
                        this.delete_permission(data.obj).then(
                            () => this.load()
                        );
                        break;
                }
            });
        }

    }

    change_weight(role_id, resource_id, perm_id, weight) {
        const headers = {
            "Accept": "application/json", 
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": this.csrf   
        };

        const body = qs.stringify({
            permission_id: perm_id,
            role_id: role_id,
            weight: weight
        }, {
            arrayFormat: 'repeat'
        });

        return new Promise((resolve, reject) => {
            fetch(this.manage_url, {
                method: 'PATCH',
                credentials: 'same-origin',
                headers: headers,
                body: body
            }).then(
                response => response.ok ? resolve(response.json()) : Promise.reject(response)
            ).catch(response => {
                console.log('===> ERROR: change_weight (permission %d, weight %d)', perm_id, weight);
            })
        });
    }

    create_permission(opts) {
        const headers = {
            "Accept": "application/json", 
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": this.csrf   
        };

        const body = qs.stringify(opts, {
            arrayFormat: 'repeat'
        });

        return new Promise((resolve, reject) => {
            fetch(this.manage_url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: headers,
                body: body
            }).then(
                response => response.ok ? resolve(response.json()) : Promise.reject(response)
            ).catch(response => {
                console.log('===> ERROR: create (permission %d)', opts.perm_id);
            })
        });
    }

    delete_permission(opts) {
        const headers = {
            "Accept": "application/json", 
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": this.csrf   
        };

        const body = qs.stringify(opts, {
            arrayFormat: 'repeat'
        });

        return new Promise((resolve, reject) => {
            fetch(this.manage_url, {
                method: 'DELETE',
                credentials: 'same-origin',
                headers: headers,
                body: body
            }).then(
                response => response.ok ? resolve(response) : Promise.reject(response)
            ).catch(response => {
                console.log('===> ERROR: delete_permission');
            })
        });
    }
}

export class ContentACLBrowser extends ACLBrowser {

    constructor(params) {
        super(params);
    }

    set_inherits_parent_acl(value) {
       const headers = {
            "Accept": "application/json", 
            "X-CSRF-Token": this.csrf   
        };

        const body = new FormData();
        body.append('inherits_parent_acl', value);

        return new Promise((resolve, reject) => {
            fetch(this.manage_url, {
                method: 'PATCH',
                credentials: 'same-origin',
                headers: headers,
                body: body
            }).then(
                response => response.ok ? resolve(response.json()) : reject(response)   
            );
        });
    }
}

export class RoleBrowser extends BrowserController {

    constructor(params) {
        super(params);
        this.csrf = params.csrf;
        this.collection_url = params.collection_url;
        
        if (!params.manage_selectors) {
            this.manage_selectors = [
                "a[data-control='delete']"
            ]
        } else {
            this.manage_selectors = params.manage_selectors;
        }
    }

    _get_data(src) {
        /* This function returns the row container (tr) and the object data */
        let data = {};

        do {
            if (src.nodeType !== 1) {
                return data;
            }

            if (src.hasAttribute('data-obj')) {
                data.obj = JSON.parse(src.getAttribute('data-obj'));
                data.container = src;
                return data;
            }

            src = src.parentNode;

        } while (1)
    }

    add_handlers() {
        const selectors = this.manage_selectors;

        for (const a of this.components.main.container.querySelectorAll(selectors)) {
            a.addEventListener('click', (e) => {
                e.preventDefault();

                const target = e.currentTarget;
                const data = this._get_data(target);

                switch (target.getAttribute('data-control')) {
                    case 'delete':
                        this.delete_role(data.obj.id).then(() => this.load())
                        break;
                }
            });
        }

    }

    delete_role(role_id) {
        const headers = {
            "Accept": "application/json", 
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": this.csrf   
        };

        const url = site_url('roles', role_id);

        return new Promise((resolve, reject) => {
            fetch(url, {
                method: 'DELETE',
                credentials: 'same-origin',
                headers: headers
            }).then(
                response => response.ok ? resolve(response) : Promise.reject(response)
            ).catch(response => {
                console.log('===> ERROR: delete_role (role_id %d)', role_id);
            })
        });
    }
}


export class RoleMemberBrowser extends BrowserController {
    constructor(params) {
        super(params);
        this.manage_url = params.manage_url;
        this.csrf = params.csrf;
        
        if (!params.manage_selectors) {
            this.manage_selectors = [
                "a[data-control='delete']",
                "a[data-control='add']"
            ]
        } else {
            this.manage_selectors = params.manage_selectors;
        }
    }

    _get_data(src) {
        /* This function returns the row container (tr) and the object data */
        let data = {};

        do {
            if (src.nodeType !== 1) {
                return data;
            }

            if (src.hasAttribute('data-obj')) {
                data.container = src;
                data.obj = JSON.parse(src.getAttribute('data-obj'));
                return data;
            } else {
                src = src.parentNode;
            }

        } while (1)
    }

    add_handlers() {
        const selectors = this.manage_selectors;

        for (const a of this.components.main.container.querySelectorAll(selectors)) {
            a.addEventListener('click', (e) => {
                e.preventDefault();

                const target = e.currentTarget;
                const data = this._get_data(target);

                switch (target.getAttribute('data-control')) {
                    case 'delete':
                        this.delete_member(data.obj.id).then(() => this.load())
                        break;
                }
            });
        }

    }

    delete_member(account_id) {
        const headers = {
            "Accept": "application/json", 
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": this.csrf   
        };

        return new Promise((resolve, reject) => {
            fetch(this.url + '/' + account_id, {
                method: 'DELETE',
                credentials: 'same-origin',
                headers: headers
            }).then(
                response => response.ok ? resolve(response) : Promise.reject(response)
            ).catch(response => {
                console.log('===> ERROR: delete_member (account_id %d)', account_id);
            })
        });
    }

    add_member(account_id) {
        const headers = {
            "Accept": "application/json", 
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": this.csrf   
        };

        const body = qs.stringify({
            account_id: account_id
        });

        return new Promise((resolve, reject) => {
            fetch(this.url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: headers,
                body: body
            }).then(
                response => response.ok ? resolve(response) : Promise.reject(response)
            ).catch(response => {
                console.log('===> ERROR: add_member (account_id %d)', account_id);
            })
        });
    }
}
